import { IPalette, ISemanticColors, PartialTheme, Theme } from '@fluentui/react';

export interface ExtendedPalette extends IPalette {
  redLight: string;
  orangeDark: string;
}

export interface ExtendedSemanticColors extends ISemanticColors {
  appBarBackground: string;
  statusBusy: string;
  statusBreak: string;
  statusAvailable: string;
  statusLoggedOut: string;
  warningText: string;
}

interface PartialAppTheme extends Omit<PartialTheme, 'palette' | 'semanticColors'> {
  palette?: Partial<ExtendedPalette>;
  semanticColors?: Partial<ExtendedSemanticColors>;
}

export interface AppTheme extends Omit<Theme, 'palette' | 'semanticColors'> {
  palette: ExtendedPalette;
  semanticColors: ExtendedSemanticColors;
}

export type AppThemed<T> = T & {
  theme?: AppTheme;
};

const customPalette: Partial<ExtendedPalette> = {
  blueLight: '#7f92c7',
  blue: '#5c74b7',
  blueDark: '#283761',
  redLight: '#d97272',
  red: '#b03e3e',
  redDark: '#a4262c',
  yellowLight: '#f9e562',
  yellow: '#d7c342',
  yellowDark: '#9d8706',
  greenLight: '#95d770',
  green: '#6db34d',
  greenDark: '#52972b',
  orangeLight: '#d19061',
  orange: '#bf7540',
  orangeDark: '#9e5d2e',
};

const customSemanticColors: Partial<ExtendedSemanticColors> = {
  appBarBackground: '#1b1c1a',
  statusBusy: '#c43148',
  statusBreak: '#ffaa44',
  statusAvailable: '#6bb700',
  statusLoggedOut: '#8a8886',
  warningText: '#b0813e',
};

export const codeFontFamily = '\'Consolas\', \'Courier New\', monospace';

export const defaultTheme: PartialAppTheme = {
  palette: customPalette,
  semanticColors: customSemanticColors,
};

export const darkTheme: PartialAppTheme = {
  isInverted: true,
  palette: {
    themeLighterAlt: '#000508',
    themeLighter: '#001322',
    themeLight: '#00243f',
    themeTertiary: '#00487f',
    themeSecondary: '#006aba',
    themePrimary: '#0078d4',
    themeDarkAlt: '#1684d8',
    themeDark: '#3595de',
    themeDarker: '#66afe7',
    black: '#ffffff',
    neutralDark: '#e5e4e3',
    neutralPrimary: '#d5d4d3',
    neutralPrimaryAlt: '#cecccb',
    neutralSecondary: '#afacaa',
    neutralSecondaryAlt: '#8b8884',
    neutralTertiary: '#777370',
    neutralTertiaryAlt: '#54514f',
    neutralQuaternary: '#4b4846',
    neutralQuaternaryAlt: '#3d3b39',
    neutralLight: '#32302f',
    neutralLighter: '#2c2b29',
    // 'neutralLighterAlt' and 'white' colors are switched
    neutralLighterAlt: '#201f1e',
    white: '#262423',
    ...customPalette,
    blueLight: customPalette.blueDark,
    blueDark: customPalette.blueLight,
    redLight: customPalette.redDark,
    redDark: customPalette.redLight,
    yellowLight: customPalette.yellowDark,
    yellowDark: customPalette.yellowLight,
    greenLight: customPalette.greenDark,
    greenDark: customPalette.greenLight,
    orangeLight: customPalette.orangeDark,
    orangeDark: customPalette.orangeLight,
  } as Partial<IPalette>,
  semanticColors: {
    ...customSemanticColors,
    errorText: '#b03e3e',
  },
};